// qs-useHistory.ts
import { useHistoryStore } from '@/stores/qs-store-history'
import { useLayerStore } from '@/stores/qs-store-layers'
import { useGalleryStore } from '@/stores/qs-store-gallery'
import { nextTick, computed, ref } from 'vue'

const UNDO_REDO_COOLDOWN_MS = 280

export function useHistory() {
  const historyStore = useHistoryStore()
  const layerStore = useLayerStore()
  const galleryStore = useGalleryStore()
  const lastUndoAt = ref(0)
  const lastRedoAt = ref(0)

  const canUndo = computed(() => historyStore.canUndo)
  const canRedo = computed(() => historyStore.canRedo)
  const hasHistory = computed(() => historyStore.hasHistory)

  const addDrawingEntry = (action: string): void => {
    historyStore.addHistoryEntry(action, layerStore.layers)
  }

  const addDeleteEntry = (action: string): void => {
    historyStore.addHistoryEntry(action, layerStore.layers)
  }

  const addLayerEntry = (action: string): void => {
    historyStore.addHistoryEntry(action, layerStore.layers)
  }

  const addTextEntry = (action: string): void => {
    historyStore.addHistoryEntry(action, layerStore.layers)
  }

  const clearHistory = (): void => {
    historyStore.clearHistoryForDrawing()
  }

  const syncWithLayers = (): void => {
    historyStore.syncWithExternalLayers(layerStore.layers)
  }

  const getHistoryStats = () => {
    return historyStore.getHistoryStats()
  }

  const initializeHistory = async (): Promise<void> => {
    const currentDrawing = galleryStore.currentDrawing
    if (!currentDrawing) return

    const cleanInitialLayers = layerStore.layers.map(layer => ({
      ...layer,
      paths: []
    }))

    historyStore.initializeForDrawing(currentDrawing.id, cleanInitialLayers)

    const hasExistingPaths = layerStore.layers.some(layer => layer.paths && layer.paths.length > 0)
    if (hasExistingPaths) {
      historyStore.addHistoryEntry('Existing drawing loaded', layerStore.layers)
    }
  }

  const performUndo = async (): Promise<void> => {
    const now = Date.now()
    if (now - lastUndoAt.value < UNDO_REDO_COOLDOWN_MS) return
    lastUndoAt.value = now

    historyStore.pauseRecording()

    const restoredLayers = historyStore.performUndo()
    if (restoredLayers) {
      layerStore.layers = [...restoredLayers]
      await nextTick()
      window.dispatchEvent(new CustomEvent('force-canvas-reinit'))
    }

    historyStore.resumeRecording()
  }

  const performRedo = async (): Promise<void> => {
    const now = Date.now()
    if (now - lastRedoAt.value < UNDO_REDO_COOLDOWN_MS) return
    lastRedoAt.value = now

    historyStore.pauseRecording()

    const restoredLayers = historyStore.performRedo()
    if (restoredLayers) {
      layerStore.layers = [...restoredLayers]
      await nextTick()
      window.dispatchEvent(new CustomEvent('force-canvas-reinit'))
    }

    historyStore.resumeRecording()
  }

  return {
    canUndo,
    canRedo,
    hasHistory,
    initializeHistory,
    addDrawingEntry,
    addDeleteEntry,
    addLayerEntry,
    addTextEntry,
    performUndo,
    performRedo,
    clearHistory,
    syncWithLayers,
    getHistoryStats
  }
}